import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import TerritorySphere from "../components/TerritorySphere";
import USMap from "../components/USMap";
import DimensionToggle, { useDimension } from "../components/DimensionToggle";
import { Check, ArrowRight, ArrowLeft, Rocket, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SolanaCheckout from "../components/SolanaCheckout";
import PayPalCheckout from "../components/PayPalCheckout";

const TIER_PRICE = { bronze: 1000, silver: 2500, gold: 5000 };

export default function BoardingDeck() {
    const navigate = useNavigate();
    const loc = useLocation();
    const qs = new URLSearchParams(loc.search);

    const [step, setStep] = useState(1);
    const [territories, setTerritories] = useState([]);
    const [selected, setSelected] = useState(null);
    const [sector, setSector] = useState("");
    const [sectors, setSectors] = useState([]);
    const [adCapital, setAdCapital] = useState(15000);
    const [busy, setBusy] = useState(false);
    const [usdcOpen, setUsdcOpen] = useState(false);
    const [dimension, , setDimension] = useDimension("3d");

    useEffect(() => {
        api.get("/territories").then(({ data }) => {
            setTerritories(data);
            const pre = qs.get("state");
            if (pre) {
                const t = data.find((x) => x.state_code === pre);
                if (t && t.status === "available") {
                    setSelected(t);
                    setStep(2);
                }
            }
        });
        api.get("/meta/sectors").then(({ data }) => setSectors(data.sectors));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!selected || !sector) return;
        setBusy(true);
        try {
            const { data } = await api.post("/payments/boarding-checkout", {
                state_code: selected.state_code,
                sector,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout failed");
            setBusy(false);
        }
    };

    const steps = ["Territory", "Sector", "Ad Capital", "Boarding Fee"];

    return (
        <div className="space-y-8">
            <div className="fade-up">
                <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">The Boarding Deck</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Board the Fleet</h1>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                    Four steps to silent partnership: choose your territory, declare your sector,
                    commit your ad-capital, and pay your Boarding Fee.
                </p>
            </div>

            {/* Stepper */}
            <div className="glass rounded-2xl p-4 md:p-6">
                <div className="flex items-center gap-3 md:gap-4 overflow-x-auto">
                    {steps.map((name, i) => {
                        const idx = i + 1;
                        const active = step === idx;
                        const done = step > idx;
                        return (
                            <React.Fragment key={name}>
                                <div className="flex items-center gap-2 whitespace-nowrap" data-testid={`step-${idx}`}>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${done ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)]" : active ? "bg-[hsl(40_61%_25%/0.8)] text-[hsl(45_85%_75%)] ring-2 ring-[hsl(40_61%_53%)]" : "bg-[hsl(151_55%_15%)] text-muted-foreground"}`}>
                                        {done ? <Check size={14} /> : idx}
                                    </div>
                                    <span className={`text-xs uppercase tracking-wider ${active ? "text-foreground" : "text-muted-foreground"}`}>{name}</span>
                                </div>
                                {i < steps.length - 1 && <span className="flex-1 h-px bg-[hsl(40_40%_30%/0.2)]" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <div className="glass rounded-2xl p-6 md:p-8 min-h-[400px]">
                {step === 1 && (
                    <div className="fade-up">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
                            <div className="font-heading text-2xl">Select your territory</div>
                            <DimensionToggle dimension={dimension} onChange={setDimension} />
                        </div>
                        <div className="text-sm text-muted-foreground mb-6">
                            {dimension === "3d"
                                ? <>Drag to rotate. Scroll to zoom. Click a <span className="text-[hsl(45_85%_70%)]">pulsing gold pulse</span> to lock the state into your fleet.</>
                                : <>Tactical 2D grid. Click a tile to lock the state into your fleet.</>}
                        </div>
                        {dimension === "3d" ? (
                            <TerritorySphere
                                territories={territories}
                                selectedCode={selected?.state_code}
                                onSelect={(t) => {
                                    if (t.status === "available") {
                                        setSelected(t);
                                        toast.success(`${t.state_name} locked into your fleet.`);
                                    } else if (t.status === "warning") {
                                        toast.warning("Advanced territory — contact the Director for clearance.");
                                    } else {
                                        toast.error("Territory already claimed.");
                                    }
                                }}
                                height={560}
                            />
                        ) : (
                            <USMap
                                territories={territories}
                                selectedCode={selected?.state_code}
                                onSelect={(t) => {
                                    if (t.status === "available") {
                                        setSelected(t);
                                        toast.success(`${t.state_name} locked into your fleet.`);
                                    } else if (t.status === "warning") {
                                        toast.warning("Advanced territory — contact the Director for clearance.");
                                    } else {
                                        toast.error("Territory already claimed.");
                                    }
                                }}
                            />
                        )}
                        {selected && (
                            <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Selected</div>
                                    <div className="font-heading text-2xl">{selected.state_name}</div>
                                    <div className="text-sm text-muted-foreground">{selected.tier?.toUpperCase()} · ${TIER_PRICE[selected.tier].toLocaleString()}</div>
                                </div>
                                <StepNav onNext={() => setStep(2)} nextDisabled={!selected} testId="step-1-next" />
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="fade-up">
                        <div className="font-heading text-2xl mb-1">Declare your sector</div>
                        <div className="text-sm text-muted-foreground mb-6">This sets your subdomain: <span className="font-mono">{selected?.state_code?.toLowerCase()}.[sector].eigooinc.net</span></div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {sectors.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSector(s)}
                                    data-testid={`sector-${s}`}
                                    className={`p-4 rounded-xl text-left transition-colors ${sector === s ? "bg-[hsl(40_61%_25%/0.6)] ring-2 ring-[hsl(40_61%_53%)]" : "bg-[hsl(151_55%_13%)] hover:bg-[hsl(151_55%_16%)]"}`}
                                >
                                    <div className="font-heading text-lg capitalize">{s}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-mono">{selected?.state_code?.toLowerCase()}.{s}</div>
                                </button>
                            ))}
                        </div>
                        <StepNav onPrev={() => setStep(1)} onNext={() => setStep(3)} nextDisabled={!sector} testId="step-2-next" />
                    </div>
                )}

                {step === 3 && (
                    <div className="fade-up">
                        <div className="font-heading text-2xl mb-1">Commit your ad-capital</div>
                        <div className="text-sm text-muted-foreground mb-6">The Fuel for your fleet. You can adjust this later in the Dashboard.</div>
                        <div className="max-w-md space-y-4">
                            <input
                                type="range"
                                min={5000}
                                max={100000}
                                step={1000}
                                value={adCapital}
                                onChange={(e) => setAdCapital(Number(e.target.value))}
                                data-testid="ad-capital-slider"
                                className="w-full accent-[hsl(40_61%_53%)]"
                            />
                            <div className="flex items-baseline gap-2">
                                <div className="font-heading text-4xl gold-gradient-text">${adCapital.toLocaleString()}</div>
                                <div className="text-sm text-muted-foreground">monthly ad spend commitment</div>
                            </div>
                        </div>
                        <StepNav onPrev={() => setStep(2)} onNext={() => setStep(4)} testId="step-3-next" />
                    </div>
                )}

                {step === 4 && selected && (
                    <div className="fade-up">
                        <div className="font-heading text-2xl mb-6">Review & pay Boarding Fee</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <Row label="Territory" value={`${selected.state_name} (${selected.state_code})`} />
                                <Row label="Sector" value={sector} />
                                <Row label="Tier" value={selected.tier?.toUpperCase()} />
                                <Row label="Subdomain" value={`${selected.state_code.toLowerCase()}.${sector}.eigooinc.net`} mono />
                                <Row label="Ad-capital commitment" value={`$${adCapital.toLocaleString()}/mo`} />
                                <Row label="Residual split" value="80 / 20 / 5" />
                            </div>
                            <div className="glass rounded-xl p-6 flex flex-col">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Due now</div>
                                <div className="font-heading text-5xl gold-gradient-text mt-1">${TIER_PRICE[selected.tier].toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground mt-1">One-time Boarding Fee</div>

                                {/* USDC primary CTA · 5% Sovereign Discount */}
                                <button
                                    onClick={() => setUsdcOpen(true)}
                                    data-testid="btn-pay-usdc-boarding"
                                    className="mt-5 w-full py-3 rounded-md font-semibold text-black flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform"
                                    style={{
                                        background: "linear-gradient(95deg, #9945FF 0%, #DC1FFF 35%, #FF6FA1 60%, #14F195 100%)",
                                    }}
                                >
                                    <Sparkles size={16} />
                                    Pay ${Math.round(TIER_PRICE[selected.tier] * 0.95).toLocaleString()} with USDC · save 5%
                                </button>
                                <div className="text-[10px] text-center mt-1.5" style={{ color: "#14F195" }}>
                                    Sovereign Discount applied · 10% diverted to CyberKids on-chain
                                </div>

                                {/* Stripe — secondary fallback */}
                                <button
                                    onClick={submit}
                                    disabled={busy}
                                    data-testid="btn-pay-boarding-fee"
                                    className="mt-3 w-full py-2.5 rounded-md bg-transparent border border-[hsl(40_50%_35%/0.5)] text-foreground/85 font-medium hover:border-[hsl(45_85%_68%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                                >
                                    <Rocket size={15} />
                                    {busy ? "Opening Stripe..." : `Card · Stripe — $${TIER_PRICE[selected.tier].toLocaleString()}`}
                                </button>
                            </div>
                        </div>
                        <div className="mt-6">
                            <StepNav onPrev={() => setStep(3)} hideNext testId="step-4" />
                        </div>

                        <SolanaCheckout
                            open={usdcOpen}
                            onClose={() => setUsdcOpen(false)}
                            amount={TIER_PRICE[selected.tier]}
                            label={`EIGOO Boarding · ${selected.tier?.toUpperCase()} · ${selected.state_code}`}
                            message={`${selected.state_code} · ${sector}`}
                            sector="finance"
                            vessel={`territory-${selected.state_code}`}
                            purpose={`Boarding · ${selected.state_code} · ${sector}`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function StepNav({ onPrev, onNext, nextDisabled, hideNext, testId }) {
    return (
        <div className="mt-8 flex items-center justify-between">
            {onPrev ? (
                <button onClick={onPrev} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={`${testId}-prev`}>
                    <ArrowLeft size={14} /> Back
                </button>
            ) : <span />}
            {!hideNext && (
                <button
                    onClick={onNext}
                    disabled={nextDisabled}
                    data-testid={testId}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                >
                    Continue <ArrowRight size={14} />
                </button>
            )}
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-4 border-b border-[hsl(40_40%_30%/0.12)] pb-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className={`text-sm text-right ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}
